import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { UserOutlined, MailOutlined, PhoneOutlined, CalendarOutlined, EditOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';

import { PageTitle } from '../../components/common/PageTitle/PageTitle';
import { useResponsive } from '../../hooks/useResponsive';
import { useTranslation } from 'react-i18next';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useActions } from '../../hooks/useActions';
import { notificationController } from '../../controllers/notificationController';

import { IStudentState } from '../../store/reducers/students/types';
import { IStudentPageState } from '../../store/reducers/student/types';
import { PageContainer, ContentCard } from '../../components/layouts/PageLayouts';
import { CreateButton } from '../../components/common/buttons';
import { StudentModal } from '../../components/common/modals';
import { Student } from '../../components/common/tables';

// Styled components
const StudentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #E0EAFC 0%, #CFDEF3 100%);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 14px;
    padding: 18px;
  }
`;

const StudentAvatar = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 26px;
  font-weight: 600;
  flex-shrink: 0;
  border: 3px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    width: 64px;
    height: 64px;
    font-size: 22px;
  }
`;

const StudentInfo = styled.div`
  flex: 1;
  position: relative;
  z-index: 1;
`;

const StudentName = styled.h1`
  margin: 0 0 2px 0;
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const StudentClass = styled.div`
  margin: 0 0 34px 0;
  
  a {
    font-size: 20px;
    color: #3498db;
    text-decoration: none;
    font-weight: 600;
    transition: all 0.2s ease;
    
    &:hover {
      color: #2980b9;
      text-decoration: underline;
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
    
    a {
      font-size: 18px;
    }
  }
`;

const StudentDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  
  @media (max-width: 768px) {
    justify-content: center;
    gap: 6px;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: rgba(255, 255, 255, 0.7);
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.9);
  transition: all 0.2s ease;
  min-width: 0;
  max-width: 200px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
  }
`;

const DetailLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: rgba(44, 62, 80, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  display: flex;
  align-items: center;
  gap: 3px;
  
  .anticon {
    font-size: 10px;
    color: rgba(44, 62, 80, 0.5);
  }
`;

const DetailValue = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: rgba(44, 62, 80, 0.9);
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StudentActions = styled.div`
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(52, 152, 219, 0.3);
  border-radius: 8px;
  color: #3498db;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.1);
  
  &:hover {
    background: #3498db;
    color: white;
    border-color: #3498db;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  .anticon {
    font-size: 14px;
  }
`;

const PerformanceOverview = styled.div`
  margin-bottom: 32px;
`;

const OverviewTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '📊';
    font-size: 24px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StatCard = styled.div<{ color: string }>`
  padding: 24px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.color};
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StatIcon = styled.div<{ bgColor: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  box-shadow: 0 4px 12px ${props => props.bgColor}40;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 4px;
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: rgba(44, 62, 80, 0.7);
  font-weight: 500;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  margin-top: 12px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ percentage: number; color: string }>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: linear-gradient(90deg, ${props => props.color} 0%, ${props => props.color}CC 100%);
  border-radius: 4px;
  transition: width 0.8s ease;
`;

// Chart Section Styles
const ChartsSection = styled.div`
  margin-bottom: 32px;
`;

const ChartsTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '📈';
    font-size: 24px;
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ChartCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

const ChartTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  
  &.pie-chart {
    height: 250px;
  }
  
  &.empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(248, 250, 252, 0.8);
    border-radius: 12px;
    border: 2px dashed rgba(0, 0, 0, 0.1);
    color: rgba(0, 0, 0, 0.4);
    font-size: 14px;
    text-align: center;
  }
`;

const FullWidthChartCard = styled(ChartCard)`
  grid-column: 1 / -1;
`;

// Test History Styles
const TestHistorySection = styled.div`
  margin-bottom: 32px;
`;

const TestHistoryTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '📋';
    font-size: 24px;
  }
`;

const TestHistoryCard = styled(ChartCard)`
  padding: 0;
  overflow: hidden;
`;

const TestTable = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 16px;
  padding: 20px 24px;
  background: rgba(248, 250, 252, 0.8);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  font-weight: 600;
  font-size: 14px;
  color: #2c3e50;
  
  @media (max-width: 768px) {
    grid-template-columns: 2fr 1fr 1fr 80px;
    gap: 12px;
    padding: 16px 20px;
    font-size: 13px;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(139, 92, 246, 0.04);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 2fr 1fr 1fr 80px;
    gap: 12px;
    padding: 16px 20px;
  }
`;

const TestName = styled.div`
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
`;

const TestDate = styled.div`
  color: rgba(44, 62, 80, 0.7);
  font-size: 13px;
`;

const TestScore = styled.div<{ score: number }>`
  font-weight: 600;
  font-size: 14px;
  color: ${props => {
    if (props.score >= 90) return '#10B981';
    if (props.score >= 80) return '#F59E0B';
    if (props.score >= 70) return '#EF4444';
    return '#6B7280';
  }};
`;

const TestStatus = styled.div<{ passed: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.passed ? '#10B981' : '#EF4444'};
  
  &::before {
    content: '${props => props.passed ? '✅' : '❌'}';
    font-size: 16px;
  }
`;

const ViewButton = styled.button`
  padding: 6px 12px;
  background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 11px;
  }
`;

const EmptyState = styled.div`
  padding: 60px 24px;
  text-align: center;
  color: rgba(0, 0, 0, 0.4);
  
  .icon {
    font-size: 48px;
    margin-bottom: 16px;
    display: block;
  }
  
  .title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
    color: rgba(0, 0, 0, 0.6);
  }
  
  .description {
    font-size: 14px;
  }
`;

// Test Details Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 0;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    max-width: 95vw;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
  
  .title {
    font-size: 20px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 8px 0;
  }
  
  .subtitle {
    font-size: 14px;
    color: rgba(44, 62, 80, 0.7);
    margin: 0 0 16px 0;
  }
  
  .close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.4);
    transition: color 0.2s ease;
    
    &:hover {
      color: rgba(0, 0, 0, 0.8);
    }
  }
`;

const ModalBody = styled.div`
  padding: 0 24px 24px 24px;
`;

const TestDetailCard = styled.div`
  background: rgba(248, 250, 252, 0.6);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  
  .detail-title {
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
    margin: 0 0 12px 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
  }
  
  .detail-item {
    .label {
      font-size: 12px;
      font-weight: 500;
      color: rgba(44, 62, 80, 0.6);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
    }
    
    .value {
      font-size: 16px;
      font-weight: 600;
      color: #2c3e50;
    }
  }
`;

const MistakesList = styled.div`
  .mistake-item {
    background: white;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
    border-left: 4px solid #EF4444;
    
    .mistake-question {
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 4px;
    }
    
    .mistake-topic {
      font-size: 12px;
      color: #8B5CF6;
      background: rgba(139, 92, 246, 0.1);
      padding: 2px 8px;
      border-radius: 4px;
      display: inline-block;
      margin-bottom: 8px;
    }
    
    .mistake-error {
      font-size: 14px;
      color: rgba(44, 62, 80, 0.8);
    }
  }
`;

const ScoreDisplay = styled.div<{ score: number }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 18px;
  background: ${props => {
    if (props.score >= 90) return 'rgba(16, 185, 129, 0.1)';
    if (props.score >= 80) return 'rgba(245, 158, 11, 0.1)';
    if (props.score >= 70) return 'rgba(239, 68, 68, 0.1)';
    return 'rgba(107, 114, 128, 0.1)';
  }};
  color: ${props => {
    if (props.score >= 90) return '#10B981';
    if (props.score >= 80) return '#F59E0B';
    if (props.score >= 70) return '#EF4444';
    return '#6B7280';
  }};
  
  &::before {
    content: '${props => {
      if (props.score >= 90) return '🎉';
      if (props.score >= 80) return '👍';
      if (props.score >= 70) return '⚠️';
      return '📚';
    }}';
    font-size: 20px;
  }
`;

const StudentPage: React.FC = () => {
  const { className, studentId } = useParams<{ className: string; studentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();

  const students = useTypedSelector((state) => state.students as IStudentState);
  const student = useTypedSelector((state) => state.student as IStudentPageState);
  const { getClassRequest, getStudentDetailsRequest, getStudentAnalyticsRequest, updateStudentRequest } = useActions();

  const [currentStudent, setCurrentStudent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState<any>(null);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  // Decode class name from URL
  const decodedClassName = className ? decodeURIComponent(className.replace(/-/g, ' ')) : '';
  const classId = searchParams.get("id") || localStorage.getItem('currentClassId');

  useEffect(() => {
    // Add userId to URL if authenticated
    const storedUserId = localStorage.getItem("UserId");
    if (storedUserId && storedUserId !== 'null' && !searchParams.get('userId')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('userId', storedUserId);
      setSearchParams(newParams, { replace: true });
    }
    
    if (studentId) {
      // Load student details
      getStudentDetailsRequest(parseInt(studentId));
      // Load analytics (grades, subjects, tests) - one request instead of multiple
      getStudentAnalyticsRequest(parseInt(studentId));
    }
  }, [studentId]);

  useEffect(() => {
    // Update current student from Redux state
    if (student.student) {
      setCurrentStudent(student.student);
      setLoading(false);
    } else if (student.error) {
      setLoading(false);
    }
  }, [student.student, student.error]);

  // Fallback: if Redux doesn't have student data, try to get from students list
  useEffect(() => {
    if (!student.student && !student.loading && classId && students.students && studentId) {
      const foundStudent = students.students.find((s: any) => s.id === parseInt(studentId));
      if (foundStudent) {
        setCurrentStudent(foundStudent);
        setLoading(false);
      } else if (classId) {
        // Load students list if not available
        getClassRequest(parseInt(classId));
      }
    }
  }, [student.student, student.loading, students.students, studentId, classId]);

  // Handle student update success
  const updateSuccess = students.update?.success;
  useEffect(() => {
    if (updateSuccess && studentId) {
      notificationController.success({
        message: 'Student Updated',
        description: 'Student information has been updated successfully.'
      });
      // Refresh student data
      getStudentDetailsRequest(parseInt(studentId));
      if (classId) {
        getClassRequest(parseInt(classId));
      }
    }
  }, [updateSuccess]);

  const handleBackToClass = () => {
    const encodedClassName = encodeURIComponent(decodedClassName.replace(/\s+/g, '-').toLowerCase());
    navigate(`/class/${encodedClassName}?id=${classId}`);
  };

  const getStudentInitials = (name: string) => {
    if (!name) return 'ST';
    const nameParts = name.trim().split(' ');
    if (nameParts.length >= 2) {
      return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
    }
    return nameParts[0].substring(0, 2).toUpperCase();
  };

  const handleEditStudent = () => {
    setEditModalVisible(true);
  };

  const handleEditModalClose = () => {
    setEditModalVisible(false);
  };

  const handleEditModalSubmit = (studentData: Partial<Student>) => {
    const nameParts = (studentData.name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    const updateStudentData = {
      id: currentStudent.id || parseInt(studentId || '0'),
      firstName,
      lastName,
      classStudentId: currentStudent.classStudentId || currentStudent.id?.toString() || '',
      classId: Number(classId),
      email: studentData.email || undefined,
      phone: studentData.phone || undefined,
      dateOfBirth: studentData.dateOfBirth || undefined
    };
    
    updateStudentRequest(updateStudentData);
    setEditModalVisible(false);
  };

  const handleTestClick = (test: any) => {
    setSelectedTest(test);
    setTestModalVisible(true);
  };

  const handleTestModalClose = () => {
    setTestModalVisible(false);
    setSelectedTest(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading || student.loading) {
    return (
      <PageContainer title="Loading..." subtitle="Please wait while we load the student information">
        <ContentCard>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>Loading student information...</div>
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  if (!currentStudent) {
    return (
      <PageContainer title="Student Not Found" subtitle="The requested student could not be found">
        <ContentCard>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <UserOutlined style={{ fontSize: '48px', color: 'rgba(0, 0, 0, 0.25)', marginBottom: '16px' }} />
            <h3>Student not found</h3>
            <p>The student you're looking for doesn't exist or has been removed.</p>
            <CreateButton onClick={handleBackToClass}>
              Back to Class
            </CreateButton>
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  const studentName = `${currentStudent.firstName || ''} ${currentStudent.lastName || ''}`.trim() || currentStudent.name || 'Unknown Student';
  
  // Get analytics data from Redux
  const analytics = student.analytics;
  
  // Process data for charts - use API data from analytics
  const processGradeData = () => {
    // Use grades from analytics
    if (analytics?.grades && analytics.grades.length > 0) {
      return analytics.grades.map((grade: any) => ({
        date: new Date(grade.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        grade: grade.score,
        subject: grade.subject
      }));
    }
    // Fallback to legacy grades array
    if (student.grades && student.grades.length > 0) {
      return student.grades.map((grade: any) => ({
        date: new Date(grade.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        grade: grade.score,
        subject: grade.subject
      }));
    }
    // Return empty array - no mock data
    return [];
  };
  
  const processSubjectData = () => {
    // Use subjects from analytics
    if (analytics?.subjects && analytics.subjects.length > 0) {
      return analytics.subjects.map((subj: any) => ({
        subject: subj.subject.length > 12 ? subj.subject.substring(0, 12) + '...' : subj.subject,
        average: subj.averageScore,
        tests: subj.testsCount
      }));
    }
    // Return empty array - no mock data
    return [];
  };
  
  const processDistributionData = () => {
    const gradeData = processGradeData();
    const distribution = { A: 0, B: 0, C: 0, D: 0, F: 0 };
    
    gradeData.forEach(item => {
      const grade = item.grade;
      if (grade >= 90) distribution.A++;
      else if (grade >= 80) distribution.B++;
      else if (grade >= 70) distribution.C++;
      else if (grade >= 60) distribution.D++;
      else distribution.F++;
    });
    
    const total = gradeData.length;
    return [
      { name: 'A (90-100)', value: distribution.A, percentage: Math.round((distribution.A / total) * 100) },
      { name: 'B (80-89)', value: distribution.B, percentage: Math.round((distribution.B / total) * 100) },
      { name: 'C (70-79)', value: distribution.C, percentage: Math.round((distribution.C / total) * 100) },
      { name: 'D (60-69)', value: distribution.D, percentage: Math.round((distribution.D / total) * 100) },
      { name: 'F (0-59)', value: distribution.F, percentage: Math.round((distribution.F / total) * 100) },
    ].filter(item => item.value > 0);
  };
  
  const gradeData = processGradeData();
  const subjectData = processSubjectData();
  const distributionData = processDistributionData();
  
  // Colors for charts
  const COLORS = ['#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#6B7280'];

  // Get test history from analytics or legacy assignments
  const testHistory = analytics?.tests || student.assignments || [];
  
  // Get stats from analytics
  const stats = analytics?.stats;

  return (
    <>
      <PageTitle 
        title={`${studentName} - Student Profile`} 
        description={`View and manage ${studentName}'s academic progress in ${decodedClassName}`} 
        url={window.location.href} 
        noIndex={true}
      />
      
      <PageContainer
        title={`Student Profile`}
        subtitle={`Academic information and progress tracking`}
      >
        <StudentHeader>
          <StudentAvatar>
            {getStudentInitials(studentName)}
          </StudentAvatar>
          
          <StudentInfo>
            <StudentName>{studentName}</StudentName>
            <StudentClass>
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  handleBackToClass();
                }}
              >
                📚 {decodedClassName}
              </a>
            </StudentClass>
            
            <StudentDetails>
              <DetailItem>
                <DetailLabel>
                  <PhoneOutlined />
                  Phone
                </DetailLabel>
                <DetailValue>
                  {currentStudent.phone || '---'}
                </DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>
                  <MailOutlined />
                  E-mail
                </DetailLabel>
                <DetailValue>
                  {currentStudent.email || '---'}
                </DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>
                  <CalendarOutlined />
                  Date of Birth
                </DetailLabel>
                <DetailValue>
                  {currentStudent.dateOfBirth 
                    ? new Date(currentStudent.dateOfBirth).toLocaleDateString()
                    : '---'
                  }
                </DetailValue>
              </DetailItem>
            </StudentDetails>
          </StudentInfo>
          
          <StudentActions>
            <ActionButton onClick={() => handleEditStudent()}>
              <EditOutlined />
              Edit Student
            </ActionButton>
          </StudentActions>
        </StudentHeader>

        <PerformanceOverview>
          <OverviewTitle>Performance Overview</OverviewTitle>
          
          <StatsGrid>
            {/* Average Score */}
            <StatCard color="#8B5CF6">
              <StatHeader>
                <StatIcon bgColor="#8B5CF6">🧮</StatIcon>
              </StatHeader>
              <StatValue>
                {student.analytics_loading ? '...' : (stats?.averageScore ? Math.round(stats.averageScore) : 'N/A')}
              </StatValue>
              <StatLabel>Average Score</StatLabel>
              {stats?.averageScore && (
                <ProgressBar>
                  <ProgressFill 
                    percentage={Math.round(stats.averageScore)} 
                    color="#8B5CF6" 
                  />
                </ProgressBar>
              )}
            </StatCard>
            
            {/* Completed Tests */}
            <StatCard color="#10B981">
              <StatHeader>
                <StatIcon bgColor="#10B981">📘</StatIcon>
              </StatHeader>
              <StatValue>
                {student.analytics_loading ? '...' : (stats?.totalTests || testHistory.length || 0)}
              </StatValue>
              <StatLabel>Completed Tests</StatLabel>
            </StatCard>
            
            {/* Last Activity */}
            <StatCard color="#3B82F6">
              <StatHeader>
                <StatIcon bgColor="#3B82F6">🕒</StatIcon>
              </StatHeader>
              <StatValue>
                {stats?.lastActivity 
                  ? new Date(stats.lastActivity).toLocaleDateString()
                  : 'N/A'
                }
              </StatValue>
              <StatLabel>Last Activity</StatLabel>
            </StatCard>
          </StatsGrid>
        </PerformanceOverview>

        <ChartsSection>
          <ChartsTitle>Academic Analytics</ChartsTitle>
          
          <ChartsGrid>
            {/* Line Chart - Grade Trends */}
            <ChartCard>
              <ChartHeader>
                <ChartTitle>📊 Grade Trends Over Time</ChartTitle>
              </ChartHeader>
              <ChartContainer className={gradeData.length === 0 ? 'empty-state' : ''}>
                {student.analytics_loading ? (
                  <div>Loading grade trends...</div>
                ) : gradeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={gradeData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        stroke="#666"
                      />
                      <YAxis 
                        domain={[0, 100]}
                        tick={{ fontSize: 12 }}
                        stroke="#666"
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                        }}
                        formatter={(value: any, name: string) => [`${value}%`, 'Grade']}
                        labelFormatter={(label) => `Date: ${label}`}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="grade" 
                        stroke="#8B5CF6" 
                        strokeWidth={3}
                        dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, stroke: '#8B5CF6', strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div>
                    <div style={{ marginBottom: '8px' }}>📈</div>
                    <div>No grade data available</div>
                    <div style={{ fontSize: '12px' }}>Complete some tests to see trends</div>
                  </div>
                )}
              </ChartContainer>
            </ChartCard>

            {/* Pie Chart - Grade Distribution */}
            <ChartCard>
              <ChartHeader>
                <ChartTitle>🍰 Grade Distribution</ChartTitle>
              </ChartHeader>
              <ChartContainer className={`pie-chart ${distributionData.length === 0 ? 'empty-state' : ''}`}>
                {student.analytics_loading ? (
                  <div>Loading distribution...</div>
                ) : distributionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={distributionData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {distributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                        }}
                        formatter={(value: any, name: string) => [`${value} tests`, 'Count']}
                      />
                      <Legend 
                        verticalAlign="bottom" 
                        height={36}
                        formatter={(value: string) => value.split(' ')[0]}
                        wrapperStyle={{ fontSize: '12px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div>
                    <div style={{ marginBottom: '8px' }}>🍰</div>
                    <div>No distribution data</div>
                    <div style={{ fontSize: '12px' }}>Need more grades for analysis</div>
                  </div>
                )}
              </ChartContainer>
            </ChartCard>
          </ChartsGrid>

          {/* Bar Chart - Performance by Subject */}
          <FullWidthChartCard>
            <ChartHeader>
              <ChartTitle>📚 Performance by Subject/Topic</ChartTitle>
            </ChartHeader>
            <ChartContainer className={subjectData.length === 0 ? 'empty-state' : ''}>
              {student.analytics_loading ? (
                <div>Loading subject performance...</div>
              ) : subjectData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={subjectData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="subject" 
                      tick={{ fontSize: 12 }}
                      stroke="#666"
                    />
                    <YAxis 
                      domain={[0, 100]}
                      tick={{ fontSize: 12 }}
                      stroke="#666"
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                      }}
                      formatter={(value: any, name: string) => {
                        if (name === 'average') return [`${value}%`, 'Average Grade'];
                        if (name === 'tests') return [`${value}`, 'Number of Tests'];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="average" 
                      fill="#10B981" 
                      name="Average Grade"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar 
                      dataKey="tests" 
                      fill="#3B82F6" 
                      name="Tests Count"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div>
                  <div style={{ marginBottom: '8px' }}>📚</div>
                  <div>No subject performance data available</div>
                  <div style={{ fontSize: '12px' }}>Complete assignments in different subjects to see comparison</div>
                </div>
              )}
            </ChartContainer>
          </FullWidthChartCard>
        </ChartsSection>

        {/* Test History Section */}
        <TestHistorySection>
          <TestHistoryTitle>Test History</TestHistoryTitle>
          <TestHistoryCard>
            <TestTable>
              <TableHeader>
                <div>Test Name</div>
                <div>Date</div>
                <div>Score</div>
                <div>Status</div>
                <div style={{ textAlign: 'center' }}>Action</div>
              </TableHeader>
              
              {student.analytics_loading ? (
                <EmptyState>
                  <span className="icon">⏳</span>
                  <div className="title">Loading test history...</div>
                </EmptyState>
              ) : testHistory.length > 0 ? (
                testHistory.map((test: any) => {
                  const score = test.score || test.grade || 0;
                  const passed = score >= 60; // Assuming 60% is passing
                  return (
                    <TableRow key={test.id} onClick={() => handleTestClick(test)}>
                      <TestName>{test.name || test.title || `Assignment ${test.id}`}</TestName>
                      <TestDate>{formatDate(test.date || test.createdAt || new Date().toISOString())}</TestDate>
                      <TestScore score={score}>{score}%</TestScore>
                      <TestStatus passed={passed}>
                        {passed ? 'Passed' : 'Failed'}
                      </TestStatus>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <ViewButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTestClick(test);
                          }}
                        >
                          View
                        </ViewButton>
                      </div>
                    </TableRow>
                  );
                })
              ) : (
                <EmptyState>
                  <span className="icon">📝</span>
                  <div className="title">No test history available</div>
                  <div className="description">Complete some tests to see your history here</div>
                </EmptyState>
              )}
            </TestTable>
          </TestHistoryCard>
        </TestHistorySection>
      </PageContainer>

      {/* Test Details Modal */}
      {testModalVisible && selectedTest && (
        <ModalOverlay onClick={handleTestModalClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <button className="close-btn" onClick={handleTestModalClose}>×</button>
              <div className="title">{selectedTest.name || selectedTest.title || 'Assignment Details'}</div>
              <div className="subtitle">
                Test taken on {formatDate(selectedTest.date || selectedTest.createdAt || new Date().toISOString())} • 
                <ScoreDisplay score={selectedTest.score || selectedTest.grade || 0} style={{ marginLeft: '8px' }}>
                  {selectedTest.score || selectedTest.grade || 0}%
                </ScoreDisplay>
              </div>
            </ModalHeader>
            
            <ModalBody>
              {/* Test Overview */}
              <TestDetailCard>
                <div className="detail-title">📊 Test Overview</div>
                <div className="detail-grid">
                  <div className="detail-item">
                    <div className="label">Total Questions</div>
                    <div className="value">{selectedTest.details?.totalQuestions || selectedTest.totalQuestions || 'N/A'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Correct Answers</div>
                    <div className="value">{selectedTest.details?.correctAnswers || selectedTest.correctAnswers || 'N/A'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Time Spent</div>
                    <div className="value">{selectedTest.details?.timeSpent || selectedTest.timeSpent || 'N/A'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Final Status</div>
                    <div className="value" style={{ 
                      color: (selectedTest.score || selectedTest.grade || 0) >= 60 ? '#10B981' : '#EF4444' 
                    }}>
                      {(selectedTest.score || selectedTest.grade || 0) >= 60 ? 'Passed' : 'Failed'}
                    </div>
                  </div>
                </div>
                
                {/* Topics Covered */}
                {selectedTest.details?.topics && selectedTest.details.topics.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <div className="label" style={{ marginBottom: '8px' }}>Topics Covered</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {selectedTest.details.topics.map((topic: string, index: number) => (
                        <span key={index} style={{
                          background: 'rgba(139, 92, 246, 0.1)',
                          color: '#8B5CF6',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </TestDetailCard>

              {/* Teacher Feedback */}
              {selectedTest.feedback && (
                <TestDetailCard>
                  <div className="detail-title">💬 Teacher Feedback</div>
                  <div style={{
                    background: 'white',
                    padding: '16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    color: '#2c3e50',
                    fontStyle: 'italic'
                  }}>
                    "{selectedTest.feedback}"
                  </div>
                </TestDetailCard>
              )}

              {/* Mistakes Analysis */}
              {selectedTest.details?.mistakes && selectedTest.details.mistakes.length > 0 && (
                <TestDetailCard>
                  <div className="detail-title">❌ Areas for Improvement</div>
                  <MistakesList>
                    {selectedTest.details.mistakes.map((mistake: any, index: number) => (
                      <div key={index} className="mistake-item">
                        <div className="mistake-question">{mistake.question}</div>
                        <div className="mistake-topic">{mistake.topic}</div>
                        <div className="mistake-error">{mistake.error}</div>
                      </div>
                    ))}
                  </MistakesList>
                </TestDetailCard>
              )}
              
              {/* Show message if no additional details available */}
              {!selectedTest.details && !selectedTest.feedback && (
                <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(0,0,0,0.4)' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
                  <div>Detailed information for this assignment is not available yet.</div>
                </div>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Edit Student Modal */}
      <StudentModal
        isOpen={editModalVisible}
        mode="edit"
        student={currentStudent ? {
          id: currentStudent.id,
          name: `${currentStudent.firstName || ''} ${currentStudent.lastName || ''}`.trim() || currentStudent.name || '',
          email: currentStudent.email || '',
          phone: currentStudent.phone || '',
          dateOfBirth: currentStudent.dateOfBirth || ''
        } : null}
        onSubmit={handleEditModalSubmit}
        onClose={handleEditModalClose}
      />
    </>
  );
};

export default StudentPage;
